<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue'
import { NSelect, selectProps } from 'naive-ui'
import type { SelectOption } from 'naive-ui'
import { getWarehouseById } from '@/api/modules/warehouse'
import { useAuthStore } from '@/stores/auth'
import { createWarehouseSelectOption } from '@/utils/warehouseSelect'

defineOptions({ inheritAttrs: false })

const props = defineProps(selectProps)
const auth = useAuthStore()

const fallbackOptions = shallowRef(new Map<string | number, SelectOption>())
const missingValues = computed(() => {
  const values = Array.isArray(props.value) ? props.value : [props.value]
  return [...new Set(values)].filter(
    (value): value is string | number =>
      value !== null &&
      value !== undefined &&
      !props.options?.some((option) => option.value === value),
  )
})

function placeholderOption(value: string | number, label: string): SelectOption {
  return { value, label, warehouseName: label, warehouseCode: '' }
}

watch(
  [
    // 筛选栏可能重建同内容的 options 数组，不因此重复补查。
    () => JSON.stringify(missingValues.value),
    () => auth.companyId,
    () => auth.userInfo?.id,
    () => auth.endpointProfileInfo,
  ],
  async (_current, _previous, onCleanup) => {
    const values = [...missingValues.value]
    const controller = new AbortController()
    let active = true
    onCleanup(() => {
      active = false
      controller.abort()
    })
    fallbackOptions.value = new Map(
      values.map((value) => [value, placeholderOption(value, '仓库名称加载中…')]),
    )
    // 只查询当前缺失的选中项，逐项加载，避免多选时并发请求放大。
    for (const value of values) {
      if (!active) return
      const id = Number(value)
      let option = placeholderOption(value, '仓库信息不可用，请重新选择')
      if (Number.isSafeInteger(id) && id > 0) {
        try {
          const warehouse = await getWarehouseById(id, controller.signal)
          if (warehouse.id === id && warehouse.warehouseName?.trim()) {
            option = { ...createWarehouseSelectOption(warehouse), value }
          }
        } catch {
          // 详情不可访问时保留原值，不显示内部错误或把 ID 当作仓库名称。
        }
      }
      if (!active) return
      fallbackOptions.value = new Map(fallbackOptions.value).set(value, option)
    }
  },
  { immediate: true },
)

function fallbackOption(value: string | number): SelectOption {
  return fallbackOptions.value.get(value) || placeholderOption(value, '仓库名称加载中…')
}
</script>

<template>
  <NSelect
    v-bind="{ ...$attrs, ...props }"
    :fallback-option="fallbackOption"
  >
    <template
      v-for="(_, name) in $slots"
      #[name]="slotProps"
    >
      <slot
        :name="name"
        v-bind="slotProps || {}"
      />
    </template>
  </NSelect>
</template>
